import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { ErrorResponse } from '@shahi-analytics/backend-utils';
import { LinesInUnitModel, LinesInUnitResponse, UnitLineDateRequest, UnitModel, UnitResponse } from '@shahi-analytics/shared-models';
import { In, Repository } from 'typeorm';
import { UnitEntity } from '../entities/unit.entity';
import { LineDayPlanEntity } from '../entities/transactions/line-plan';
import { OperationEntity } from '../entities/operation.entity';
import { OperatorEntity } from '../entities/operator.entity';
import { WorkstationEntity } from '../entities/workstation.entity';
import { LineEntity } from '../entities/line.entity';
import { ReasonEntity } from '../entities/reason.entity';
import { StyleEntity } from '../entities/style.entity';

@Injectable()
export class InaHelperService {

    constructor(
        @InjectRepository(UnitEntity) private unitEnt: Repository<UnitEntity>,
        @InjectRepository(LineDayPlanEntity) private linePlanEnt: Repository<LineDayPlanEntity>,
        @InjectRepository(OperatorEntity) private operatorEnt: Repository<OperatorEntity>,
        @InjectRepository(OperationEntity) private operationEnt: Repository<OperationEntity>,
        @InjectRepository(WorkstationEntity) private workEnt: Repository<WorkstationEntity>,
        @InjectRepository(LineEntity) private lineEnt: Repository<LineEntity>,
        @InjectRepository(ReasonEntity) private reasonEntity: Repository<ReasonEntity>,
        @InjectRepository(StyleEntity) private styleEnt: Repository<StyleEntity>,
    ) { }


    async getAllUnits(): Promise<UnitResponse> {
        const unitModels: UnitModel[] = [];
        const units = await this.unitEnt.find({ where: { isActive: true} });
        if(units.length == 0) {
            throw new ErrorResponse(0, 'No units found');
        }
        units.forEach(u => {
            unitModels.push( new UnitModel(u.id, u.unitId, u.unitName) );
        });
        return new UnitResponse(true, 0, 'Units retrieved', unitModels);
    }

    /**
     * 
     * @param req 
     */
    async getLinesInUnit(req: UnitLineDateRequest): Promise<LinesInUnitResponse> {
        const linesModels: LinesInUnitModel[] = [];
        const lines = await this.lineEnt.find({ select: ['lineId', 'lineNumber', 'lineDesc'], where: { unitId: req.unit, isActive: true } });
        lines.forEach(rec => {
        linesModels.push(new LinesInUnitModel(rec.lineId, rec.lineNumber, rec.lineDesc));
        });
        return new LinesInUnitResponse(true, 0, 'Lines in unit retrieved successfully', linesModels);
    }
    
    /**
     * 
     * @param unitId 
     * @param lineIds 
     * @returns Map of line -> style -> hour -> plan quantity
     */
    async getPlantQuantitiesStyleWise(unitId: string, lineIds: string[], date: string): Promise<Map<string, Map<string, Map<number, number>>>> {
        const planQtyMap = new Map<string, Map<string, Map<number, number>>>();
        const query = await this.linePlanEnt.createQueryBuilder('l')
        .select('line_id, style_id, hour, plan_date, plan_quantity')
        .where(` unit_id = '${unitId}' AND plan_date = '${date}' `);
        if(lineIds.length > 0) {
            query.andWhere(` line_id IN (:...lineIds)`, { lineIds: lineIds});
        }
        query.orderBy('unit_id, line_id * 1');
        const result: {line_id: string, style_id: string, hour: number, plan_date: string, plan_quantity: number}[] = await query.getRawMany();

        result.forEach(rec => {
            if (!planQtyMap.has(rec.line_id))
                planQtyMap.set(rec.line_id, new Map<string, Map<number, number>>());

            if (!planQtyMap.get(rec.line_id).has(rec.style_id))
                planQtyMap.get(rec.line_id).set(rec.style_id, new Map<number, number>());

            planQtyMap.get(rec.line_id).get(rec.style_id).set(rec.hour, Number(rec.plan_quantity));
        });
        return planQtyMap;
    }

  /**
   * 
   * @param unitId 
   * @param lineIds 
   * @returns Map of line -> hour -> plan quantity
   */
    async getPlantQuantitiesHourWise(unitId: string, lineIds: string[], date: string): Promise<Map<string, Map<number, number>>> {
        const planQtyMap = new Map<string, Map<number, number>>();
        const query = await this.linePlanEnt.createQueryBuilder('l')
        .select('line_id, style_id, hour, plan_date, plan_quantity')
        .where(` unit_id = '${unitId}' AND plan_date = '${date}' `);
        if(lineIds.length > 0) {
            query.andWhere(` line_id IN (:...lineIds)`, { lineIds: lineIds});
        }
        query.orderBy('unit_id, line_id * 1');
        const result: {line_id: string, style_id: string, hour: number, plan_date: string, plan_quantity: number}[] = await query.getRawMany();

        result.forEach(rec => {
            if (!planQtyMap.has(rec.line_id))
                planQtyMap.set(rec.line_id, new Map<number, number>());

            if (!planQtyMap.get(rec.line_id).has(rec.hour))
                planQtyMap.get(rec.line_id).set(rec.hour, 0);

            const preQty = planQtyMap.get(rec.line_id).get(rec.hour);
            planQtyMap.get(rec.line_id).set(rec.hour, Number(rec.plan_quantity) + preQty);
        });
        return planQtyMap;
    }

    /**
   * 
   * @param unit 
   */
    async getOperationsInfo(unit: string): Promise<Map<string, Map<string, OperationEntity>>> {
        const opMap = new Map<string, Map<string, OperationEntity>>();
        const operations = await this.operationEnt.find({ select: ['lineId', 'operationId', 'operationCode', 'operationDesc'], where: { unitId: unit } });
        operations.forEach(rec => {
          if (!opMap.has(rec.lineId)) {
            opMap.set(rec.lineId, new Map<string, OperationEntity>());
          }
          opMap.get(rec.lineId).set(rec.operationId, rec);
        });
        return opMap;
    }

    /**
     * gets the workstation info
     * @param unit 
     * @returns 
     */
    async getWorkstationInfo(unit: string): Promise<Map<string, Map<string, WorkstationEntity>>> {
        const workstationMap = new Map<string, Map<string, WorkstationEntity>>();
        const workstations = await this.workEnt.find({ select: ['workstationId', 'workstationDesc', 'lineId'], where: { unitId: unit } });
        workstations.forEach(rec => {
        if (!workstationMap.has(rec.lineId)) {
            workstationMap.set(rec.lineId, new Map<string, WorkstationEntity>());
        }
        workstationMap.get(rec.lineId).set(rec.workstationId, rec);
        });
        return workstationMap;
    }

    /**
     * 
     * @param unitId 
     * @returns 
     */
    async getReasons(unitId: string): Promise<Map<string, Map<string, ReasonEntity>>> {
        const reasonsMap = new Map<string, Map<string, ReasonEntity>>();
        const reasons = await this.reasonEntity.find({ select: ['lineId', 'reasonDesc', 'reasonKey', 'reasonCode', 'level'], where: { unitId: unitId } });

        reasons.forEach(rec => {
        if (!reasonsMap.has(rec.lineId)) {
            reasonsMap.set(rec.lineId, new Map<string, ReasonEntity>());
        }
        reasonsMap.get(rec.lineId).set(rec.reasonKey, rec);
        });
        return reasonsMap;
    }


    /**
     * 
     * @param unit 
     * @param operatorIds 
     */
    async getOperatorsInfo(unit: string, operatorIds: string[]): Promise<Map<string, Map<string, OperatorEntity>>> {
        const operatorInfoMap = new Map<string, Map<string, OperatorEntity>>();
        const operators = await this.operatorEnt.find({ select: ['lineId', 'operatorId', 'operatorName'], where: { unitId: unit, operatorId: In(operatorIds) } });
        operators.forEach(op => {
        if (!operatorInfoMap.has(op.lineId)) {
            operatorInfoMap.set(op.lineId, new Map<string, OperatorEntity>());
        }
        operatorInfoMap.get(op.lineId).set(op.operatorId, op);
        });
        return operatorInfoMap;
    }

    /**
     * 
     * @param styleIds 
     * @returns 
     */
    async getStyleInfo(unitId: string, styleIds: string[]): Promise<Map<string, Map<string, StyleEntity>>> {
        const styleInfoMap = new Map<string, Map<string, StyleEntity>>();
        const stylesInfo = await this.styleEnt.find({ select: ['lineId', 'styleId', 'styleDesc', 'styleName'], where: { unitId: unitId, styleId: In(styleIds) } });
        stylesInfo.forEach(rec => {
        if (!styleInfoMap.has(rec.lineId)) {
            styleInfoMap.set(rec.lineId, new Map<string, StyleEntity>());
        }
        styleInfoMap.get(rec.lineId).set(rec.styleId, rec);
        });
        return styleInfoMap;
    }

    /**
     * 
     * @param styleIds 
     * @returns 
     */
    async getLineInfo(unitId: string, lineIds?: string[]): Promise<Map<string, LineEntity>> {
        const lineInfoMap = new Map<string, LineEntity>();
        let linesInfo;
        if (lineIds?.length > 0) {
            linesInfo = await this.lineEnt.find({ select: ['lineId', 'lineDesc'], where: { unitId: unitId, lineId: In(lineIds) } });
        } else {
            linesInfo = await this.lineEnt.find({ select: ['lineId', 'lineDesc'], where: { unitId: unitId } });
        }
        linesInfo.forEach(rec => {
            lineInfoMap.set(rec.lineId, rec);
        });
        return lineInfoMap;
    }

    /**
     * 
     * @param unitId 
     * @param styleIds 
     * @param date 
     */
    async getBuyerInfo(unitId: string, styleIds: string[], date: string): Promise<Map<string, string>> {
        const buyerMap = new Map<string, string>();
        const buyersInfo = await this.linePlanEnt.find({ select: ['styleId', 'buyer'], where: { unitId: unitId, styleId: In(styleIds), planDate: date } });
        buyersInfo.forEach(rec => {
            buyerMap.set(rec.styleId, rec.buyer);
        });
        return buyerMap;
    }
}

